import type { KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { useT } from '../i18n';

/**
 * Printed deck rule (from Cartes.pdf, 55 cards):
 * cards 1–40 are "Base", cards 41–55 are "Supplémentaire".
 */
export const BASE_CARD_MAX = 40;

/**
 * Whether a printed card number belongs to the base deck.
 * Returns null when the number is unknown (topic not in the printed deck).
 */
export function isBaseCard(pdfCardNumber: number | null | undefined): boolean | null {
  if (pdfCardNumber == null) return null;
  return pdfCardNumber <= BASE_CARD_MAX;
}

export type TopicCardSize = 'sm' | 'md';

export interface TopicCardProps {
  /** Topic title, printed in the centre of the card face. */
  name: string;
  /** Printed card number (1–55). Also determines the Base/Supplémentaire label. */
  number?: number | null;
  /** Category colour (hex) for the bottom-left dot. Omitted → no dot. */
  color?: string | null;
  /** 'sm' for pickers/chips, 'md' (default) for detail views. Same layout, scaled. */
  size?: TopicCardSize;
  /** Draw an outer selection ring (the card face itself never changes). */
  selected?: boolean;
  /** 1-based pick-order badge overlaid on the top-left corner. */
  order?: number | null;
  /** Recurrence count — renders a "×n" badge when ≥ 2 (top-left corner). */
  recurrence?: number | null;
  /** Tooltip for the recurrence badge. */
  recurrenceTitle?: string;
  /** Makes the card clickable (renders focusable, with hover elevation). */
  onClick?: () => void;
  /** Shows a small remove button overlaid on the top-right corner. */
  onRemove?: () => void;
  className?: string;
}

/**
 * On-screen facsimile of a printed Espace Evolution card:
 * landscape 471×347, white face, thin border, title centred,
 * bottom-left "● <number> Base|Supplémentaire", bottom-right "L'Eveil".
 * The face stays white with dark text in both app themes.
 */
export function TopicCard({
  name,
  number,
  color,
  size = 'md',
  selected = false,
  order,
  recurrence,
  recurrenceTitle,
  onClick,
  onRemove,
  className,
}: TopicCardProps) {
  const { t } = useT();
  const base = isBaseCard(number);

  const classes = [
    'topic-card',
    `topic-card--${size}`,
    selected && 'topic-card--selected',
    onClick && 'topic-card--clickable',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const face = (
    <>
      {order != null && <span className="topic-card-order">{order}</span>}
      {recurrence != null && recurrence >= 2 && (
        <span className="topic-card-recurrence" title={recurrenceTitle}>×{recurrence}</span>
      )}
      <span className="topic-card-title">{name}</span>
      <span className="topic-card-footer">
        <span className="topic-card-footer-left">
          {color && <span className="topic-card-dot" style={{ background: color }} />}
          {number != null && <span className="topic-card-num">{number}</span>}
          {base != null && (
            <span className="topic-card-kind">
              {base ? t('cards.base') : t('cards.supplementary')}
            </span>
          )}
        </span>
        <span className="topic-card-brand">{t('cards.brand')}</span>
      </span>
      {onRemove && (
        <button
          type="button"
          className="topic-card-remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={t('cards.remove')}
        >
          <X size={11} />
        </button>
      )}
    </>
  );

  if (onClick && !onRemove) {
    return (
      <button
        type="button"
        className={classes}
        onClick={onClick}
        title={name}
        aria-pressed={selected}
      >
        {face}
      </button>
    );
  }

  if (onClick) {
    // Both onClick and onRemove: a <button> cannot nest a <button>.
    const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    };
    return (
      <div
        className={classes}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={onKeyDown}
        title={name}
        aria-pressed={selected}
      >
        {face}
      </div>
    );
  }

  return (
    <div className={classes} title={name}>
      {face}
    </div>
  );
}
